<template>
  <div id="app">
    <nav class="navbar">
      <div class="nav-brand">
        <router-link to="/">
          <font-awesome-icon :icon="brandIcon" />
          {{ brandName }}
        </router-link>
      </div>
      <div class="nav-links">
        <router-link to="/" class="nav-link">Home</router-link>
        <router-link to="/about" class="nav-link">About</router-link>
        <template v-if="isLoggedIn">
          <router-link to="/write" class="nav-link">Write Letter</router-link>
          <router-link to="/my-letters" class="nav-link">My Letters</router-link>
          <button @click="$emit('sign-out')" class="nav-link sign-out">
            <font-awesome-icon icon="sign-out-alt" /> Sign Out
          </button>
        </template>
        <template v-else>
          <button @click="$emit('sign-in')" class="nav-link sign-in">
            Sign In
          </button>
        </template>
      </div>
    </nav>

    <main class="main-content">
      <slot />
    </main>

    <footer class="footer">
      <p class="footer-tagline">{{ footerTagline }}</p>
      <div class="footer-links">
        <router-link to="/privacy">Privacy Policy</router-link>
        <span class="separator">|</span>
        <router-link to="/terms">Terms of Service</router-link>
        <span class="separator">|</span>
        <a :href="`mailto:${contactEmail}`">Contact</a>
      </div>
      <p class="footer-copyright">&copy; {{ currentYear }} {{ copyrightName }}</p>
    </footer>
  </div>
</template>

<script>
// Shared app chrome (nav + footer) for the letter-composer apps. The nav links
// are the same across apps; only brand/footer text and the theme differ — text
// comes from props, the theme from --tou-* CSS custom properties (defaults below
// are the congress slate palette; letters overrides them on :root). Auth is
// decoupled: this emits 'sign-in'/'sign-out' and the host app runs its store
// action + router redirect.
export default {
  name: 'AppShell',
  props: {
    brandName: { type: String, required: true },
    brandIcon: { type: String, required: true },
    footerTagline: { type: String, default: '' },
    contactEmail: { type: String, required: true },
    copyrightName: { type: String, required: true },
    isLoggedIn: { type: Boolean, default: false },
  },
  emits: ['sign-in', 'sign-out'],
  computed: {
    currentYear() {
      return new Date().getFullYear();
    },
  },
};
</script>

<style>
/*
 * Global app chrome + design system, themed via --tou-* custom properties.
 * Defaults are the congress slate palette; an app re-skins by setting --tou-*
 * on :root (see the letters app for the warm/serif override set).
 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: var(--tou-font-body, 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif);
  background-color: var(--tou-bg, #f8fafc);
  color: var(--tou-text, #1e293b);
  line-height: 1.6;
}

#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.navbar {
  background: linear-gradient(135deg, var(--tou-primary-dark, #1e40af) 0%, var(--tou-primary, #3b82f6) 100%);
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: var(--tou-navbar-shadow, 0 2px 4px rgba(0, 0, 0, 0.1));
}

.nav-brand a {
  color: var(--tou-on-primary, white);
  text-decoration: none;
  font-family: var(--tou-font-brand, inherit);
  font-size: 1.5rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.nav-link {
  color: var(--tou-nav-link, rgba(255, 255, 255, 0.9));
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  font-family: var(--tou-font-body, inherit);
}

.nav-link:hover {
  color: var(--tou-on-primary, white);
}

.nav-link.router-link-active {
  color: var(--tou-on-primary, white);
  border-bottom: 2px solid var(--tou-on-primary, white);
  padding-bottom: 2px;
}

.sign-in {
  background: var(--tou-on-primary, white);
  color: var(--tou-primary-dark, #1e40af);
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 600;
}

.sign-out {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.main-content {
  flex: 1;
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
}

.footer {
  background: var(--tou-footer-bg, #1e293b);
  color: var(--tou-footer-text, #94a3b8);
  text-align: center;
  padding: 1.5rem;
  margin-top: auto;
}

.footer-tagline {
  font-family: var(--tou-font-brand, inherit);
  font-style: var(--tou-tagline-style, normal);
  margin-bottom: 0.75rem;
}

.footer-links {
  margin-bottom: 0.75rem;
}

.footer-links a {
  color: var(--tou-footer-text, #94a3b8);
  text-decoration: none;
  transition: color 0.2s;
}

.footer-links a:hover {
  color: var(--tou-on-primary, white);
}

.footer-links .separator {
  margin: 0 0.75rem;
  color: var(--tou-footer-separator, #475569);
}

.footer-copyright {
  font-size: 0.85rem;
  color: var(--tou-footer-copyright, #64748b);
}

/* Common button styles */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  font-family: var(--tou-font-body, inherit);
}

.btn-primary {
  background: linear-gradient(135deg, var(--tou-primary-dark, #1e40af) 0%, var(--tou-primary, #3b82f6) 100%);
  color: var(--tou-on-primary, white);
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px var(--tou-primary-shadow, rgba(59, 130, 246, 0.4));
}

.btn-secondary {
  background: var(--tou-btn-secondary-bg, #e2e8f0);
  color: var(--tou-btn-secondary-text, #475569);
}

.btn-secondary:hover {
  background: var(--tou-btn-secondary-hover, #cbd5e1);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Form styles */
.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: var(--tou-form-label, #374151);
}

.form-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid var(--tou-form-border, #e2e8f0);
  border-radius: 8px;
  font-size: 1rem;
  font-family: var(--tou-font-body, inherit);
  background: var(--tou-input-bg, white);
  transition: border-color 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: var(--tou-accent, #3b82f6);
}

.form-textarea {
  min-height: 120px;
  resize: vertical;
}

/* Card styles */
.card {
  background: var(--tou-card-bg, white);
  border-radius: 12px;
  box-shadow: var(--tou-card-shadow, 0 1px 3px rgba(0, 0, 0, 0.1));
  padding: 1.5rem;
}

@media (max-width: 768px) {
  .navbar {
    flex-direction: column;
    gap: 1rem;
  }

  .nav-links {
    flex-wrap: wrap;
    justify-content: center;
  }

  .main-content {
    padding: 1rem;
  }
}
</style>
